import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  Input,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import { createSelector, Store } from '@ngrx/store';
import { Observable } from 'rxjs';

import { Vehicle, VehicleSummary } from 'src/app/api/owcc/models';
import { SummarySidebarComponent } from 'src/app/features/configuration/summary-view/summary-sidebar/summary-sidebar.component';
import {
  fromMq,
  selectConfigurationVehicle,
  selectIsSummarySidebarShown,
  selectOrFetchVehicleSummaryForCurrentVehicleId
} from 'src/app/store/common/common.selectors';
import { selectIsMotorizationOrSummary, selectIsSummaryPage } from 'src/app/store/router/router.selectors';
import { APP_CONTAINER_ID } from 'src/app/utils/global-constants';

import { SummaryVehicleDataComponent } from '../../../features/configuration/summary-view/summary-sidebar/summary-vehicle-data/summary-vehicle-data.component';
import { DisclaimerCanvasComponent } from '../../disclaimer-canvas/disclaimer-canvas.component';
import { NbaBarModule } from '../../nba-bar/nba-bar.module';
import { MainStageComponent, Stickiness } from '../main-stage/main-stage.component';

const DEFAULT_NUMBER_OF_COLUMNS = 12;
const SIDEBAR_COLUMNS_MQ4 = 5;
const SIDEBAR_COLUMNS_MQ5 = 4;
const SIDEBAR_COLUMNS_MQ6 = 4;

const selectNumberOfColumns = createSelector(
  selectIsSummarySidebarShown,
  fromMq.selectIsMq7,
  (isSidebarShown, isMq7) => {
    const sidebarColumns: number[] = [SIDEBAR_COLUMNS_MQ4, SIDEBAR_COLUMNS_MQ5, SIDEBAR_COLUMNS_MQ6];

    if (isSidebarShown) {
      const stageColumns = sidebarColumns.map(value => DEFAULT_NUMBER_OF_COLUMNS - value);

      if (isMq7) {
        // This is needed as wb-grid does not take mq7 attribute into account.
        sidebarColumns[2] = 3;
        stageColumns[2] = DEFAULT_NUMBER_OF_COLUMNS - sidebarColumns[2];
      }

      return { stageColumns, sidebarColumns };
    }

    // For the case no sidebar
    return {
      stageColumns: sidebarColumns.map(() => DEFAULT_NUMBER_OF_COLUMNS),
      sidebarColumns: sidebarColumns.map(() => 0)
    };
  }
);

@Component({
  selector: 'cc-stage-with-sidebar',
  templateUrl: './stage-with-sidebar.component.html',
  styleUrl: './stage-with-sidebar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MainStageComponent,
    NbaBarModule,
    DisclaimerCanvasComponent,
    SummarySidebarComponent,
    SummaryVehicleDataComponent
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class StageWithSidebarComponent implements OnChanges {
  @Input()
  public stickiness: Stickiness | undefined;

  @Input()
  public hasSelectionBoxLeftNextToIt!: boolean;

  public readonly appContainerId = APP_CONTAINER_ID;

  public stickyStageStyle = {};

  public readonly defaultNumberOfColumns = DEFAULT_NUMBER_OF_COLUMNS;

  public readonly isSidebarShown$: Observable<boolean> = this.store.select(selectIsSummarySidebarShown);

  public readonly numberOfColumns$ = this.store.select(selectNumberOfColumns);

  public readonly isDisclaimerCanvasShown$ = this.store.select(selectIsMotorizationOrSummary);

  public readonly isNbaBarShown$ = this.store.select(selectIsSummaryPage);

  public readonly isSummaryPage$ = this.store.select(selectIsSummaryPage);

  public readonly vehicle$: Observable<Vehicle | null> = this.store.select(selectConfigurationVehicle);

  public readonly vehicleSummary$: Observable<VehicleSummary | undefined> = this.store.pipe(
    selectOrFetchVehicleSummaryForCurrentVehicleId
  );

  public constructor(private readonly store: Store) {}

  public ngOnChanges({ stickiness }: SimpleChanges): void {
    if (stickiness?.currentValue) {
      this.stickyStageStyle = {
        position: 'sticky',
        top: `${(stickiness.currentValue as Stickiness).topPosition}px`
      };
    }
  }
}
